/**
 * 🔒 AdminBossCodex — owner-only boss dossier preview.
 *
 * Fetches `/api/dragonious-fighters/roster/admin-full` and renders every
 * backend-known boss (Chronophage Echo, Gail Bloom, Win-Win, Dr. Cosmos
 * Prism, etc.) with lock/CPU/boss flags, titles, archetype, passives
 * and specials — WITHOUT launching a fight.
 *
 * Owner directive Feb 18, 2026: "Add a small 'boss preview' tab on the
 * DF roster page so owners can inspect Chronophage and Dr. Cosmos Prism
 * dossiers without launching a fight."
 *
 * Feb 2026 update: Win-Win removed from the codex — she is an alt-timeline
 * Gail, NOT a boss (see WIN_WIN_ALT_TIMELINE_GAIL_CANON_FEB2026.md).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Shield, Eye, ChevronDown, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOSS_ICON = {
  gail_bloom:      "🌸",
  chronophage_echo: "⏳",
  dr_cosmos_prism: "🌌",
};
const BOSS_ACCENT = {
  gail_bloom:      "from-emerald-950/40 to-teal-900/20 border-emerald-800/50 text-emerald-200",
  chronophage_echo: "from-violet-950/40 to-fuchsia-900/20 border-violet-800/50 text-violet-200",
  dr_cosmos_prism: "from-indigo-950/50 to-slate-900/30 border-indigo-700/60 text-indigo-100",
};

function KeyValueRow({ label, value }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-2 text-[11px] py-0.5">
      <span className="text-neutral-500 uppercase tracking-wider text-[9px] font-bold min-w-[90px]">{label}</span>
      <span className="text-neutral-200">{
        Array.isArray(value) ? value.join(" · ") :
        typeof value === "object" ? JSON.stringify(value) :
        String(value)
      }</span>
    </div>
  );
}

function BossCard({ id, boss, expanded, onToggle }) {
  const icon = BOSS_ICON[id] || "💀";
  const accent = BOSS_ACCENT[id] || "from-neutral-900/40 to-neutral-950/20 border-neutral-800 text-neutral-200";
  const name = boss.display || boss.name || id;
  const titles = boss.titles || [];
  const passives = boss.passives || [];
  const specials = boss.special_moves || boss.specials || [];

  return (
    <div className={`rounded-xl border bg-gradient-to-br ${accent} overflow-hidden`}
         data-testid={`admin-boss-card-${id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/5 transition-colors"
        data-testid={`admin-boss-toggle-${id}`}
      >
        <div className="text-3xl">{icon}</div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-[0.3em] text-current/60 mb-0.5 font-bold">
            {boss.availability?.mode || "boss"} · {passives.length}P · {specials.length}S
          </div>
          <div className="text-sm font-bold truncate">{name}</div>
          {titles.length > 0 && (
            <div className="text-[10px] italic text-current/70 truncate">
              {titles.join(" · ")}
            </div>
          )}
        </div>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {expanded && (
        <div className="border-t border-current/20 bg-black/30 p-3 space-y-2">
          <KeyValueRow label="Fighter ID" value={boss.fighter_id || id} />
          <KeyValueRow label="Archetype"  value={boss.archetype} />
          <KeyValueRow label="Tagline"    value={boss.tagline || boss.signature_catchphrase} />
          <KeyValueRow label="Availability" value={boss.availability?.mode} />
          <KeyValueRow label="Suppressed ID" value={boss.suppressed_identity?.human_name} />
          <KeyValueRow label="Player Locked" value={boss.player_locked ? "yes" : "no"} />
          <KeyValueRow label="Is Boss"     value={boss.is_boss ? "yes" : boss.is_mini_boss ? "mini" : null} />

          {passives.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-current/60 font-bold mb-1">
                🛡️ Passives ({passives.length})
              </div>
              <div className="space-y-1.5">
                {passives.slice(0, 6).map((p, idx) => (
                  <div key={idx} className="rounded bg-black/40 border border-current/20 p-2">
                    <div className="text-[11px] font-bold text-current">
                      {p.name || p.title || `Passive ${idx + 1}`}
                    </div>
                    {p.description && (
                      <div className="text-[10px] text-neutral-300 mt-0.5 leading-snug">
                        {p.description}
                      </div>
                    )}
                  </div>
                ))}
                {passives.length > 6 && (
                  <div className="text-[10px] text-current/60 italic">
                    …+{passives.length - 6} more
                  </div>
                )}
              </div>
            </div>
          )}

          {specials.length > 0 && (
            <div className="mt-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-current/60 font-bold mb-1">
                ⚔️ Specials ({specials.length})
              </div>
              <div className="space-y-1.5">
                {specials.slice(0, 6).map((s, idx) => (
                  <div key={idx} className="rounded bg-black/40 border border-current/20 p-2">
                    <div className="text-[11px] font-bold text-current flex items-center gap-2">
                      {s.name || `Move ${idx + 1}`}
                      {s.input && (
                        <span className="text-[9px] text-current/60 font-mono">
                          [{s.input}]
                        </span>
                      )}
                    </div>
                    {s.description && (
                      <div className="text-[10px] text-neutral-300 mt-0.5 leading-snug">
                        {s.description}
                      </div>
                    )}
                  </div>
                ))}
                {specials.length > 6 && (
                  <div className="text-[10px] text-current/60 italic">
                    …+{specials.length - 6} more
                  </div>
                )}
              </div>
            </div>
          )}

          {(boss.canon_docs?.length > 0) && (
            <div className="mt-2 pt-2 border-t border-current/20">
              <div className="text-[9px] uppercase tracking-[0.3em] text-current/60 font-bold mb-1">
                📖 Canon Files
              </div>
              {boss.canon_docs.map((doc, i) => (
                <div key={i} className="text-[10px] text-neutral-400 font-mono truncate">
                  {doc}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminBossCodex({ userRole }) {
  const isOwner = (userRole || "").toLowerCase() === "owner";
  const [open, setOpen] = useState(false);
  const [bosses, setBosses] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || bosses !== null) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dragonious-fighters/roster/admin-full`);
        // 🚫 Feb 2026: Win-Win is alt-timeline Gail, not a boss.
        // Filter defensively in case any stale data still surfaces her here.
        const allBosses = data.bosses || {};
        const filtered = Object.fromEntries(
          Object.entries(allBosses).filter(([bid]) => bid !== "win_win")
        );
        if (alive) setBosses(filtered);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, bosses]);

  if (!isOwner) return null;

  return (
    <div className="mb-6" data-testid="admin-boss-codex">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 rounded-xl border border-red-800/60 bg-gradient-to-r from-red-950/40 via-neutral-950/60 to-orange-950/40 px-4 py-3 hover:from-red-900/40 hover:to-orange-900/40 transition-colors"
        data-testid="admin-boss-codex-toggle"
      >
        <div className="flex items-center gap-3">
          <Shield size={18} className="text-red-400" />
          <div className="text-left">
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-300/80 font-bold">
              🔒 Admin Only
            </div>
            <div className="text-sm text-red-100 font-bold flex items-center gap-2">
              <Eye size={14} /> Boss Codex Preview
            </div>
          </div>
        </div>
        <div className="text-[10px] text-red-300/60 font-mono">
          {open ? "close" : "open"}
        </div>
      </button>

      {open && (
        <div className="mt-3 rounded-xl border border-red-900/50 bg-black/60 p-4"
             data-testid="admin-boss-codex-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-xs text-red-300/80 font-bold uppercase tracking-widest">
                Boss Codex · Backend Dossier Preview
              </div>
              <div className="text-[10px] text-neutral-400 mt-0.5">
                Inspect every backend-known boss without launching a fight. Owner-only.
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 rounded hover:bg-white/10 text-neutral-400 hover:text-white"
              data-testid="admin-boss-codex-close"
            >
              <X size={14} />
            </button>
          </div>

          {loading && (
            <div className="text-center text-[11px] text-neutral-500 py-6">
              Loading dossiers…
            </div>
          )}
          {error && (
            <div className="text-center text-[11px] text-red-400 py-6">
              {error}
            </div>
          )}
          {!loading && !error && bosses && Object.keys(bosses).length === 0 && (
            <div className="text-center text-[11px] text-neutral-500 py-6">
              No bosses returned from the server.
            </div>
          )}
          {!loading && !error && bosses && Object.keys(bosses).length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(bosses).map(([id, boss]) => (
                <BossCard
                  key={id}
                  id={id}
                  boss={boss}
                  expanded={expandedId === id}
                  onToggle={() => setExpandedId((cur) => (cur === id ? null : id))}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
